import { useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import {
  applyBrandByProfileToDom,
  type BrandProfileKey,
} from '../../theme/profileBrandColors';

function hexToHslNumbers(hex: string): string {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) return '';

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}

export function ThemeLoader() {
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    const fetchAndApplyTheme = async () => {
      try {
        let response;
        if (isAuthenticated && user) {
          response = await api.get('/estilo/config');
        } else {
          // Rota pública resolver
          const urlParams = new URLSearchParams(window.location.search);
          const escolaId = urlParams.get('escolaId') || '';
          const municipioId = urlParams.get('municipioId') || '';
          const estadoId = urlParams.get('estadoId') || '';
          const redeId = urlParams.get('redeId') || '';
          
          response = await api.get('/estilo/config/resolve', {
            params: { escolaId, municipioId, estadoId, redeId }
          });
        }

        const data = response.data;
        if (!data) return;

        // Injetar variáveis CSS em <style id="dynamic-theme-vars">
        let styleTag = document.getElementById('dynamic-theme-vars') as HTMLStyleElement;
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = 'dynamic-theme-vars';
          document.head.appendChild(styleTag);
        }

        const parseAndFormatColors = (colorsJson: string) => {
          let css = '';
          try {
            const colorsObj = JSON.parse(colorsJson);
            Object.entries(colorsObj).forEach(([key, val]) => {
              if (typeof val === 'string' && val.startsWith('#')) {
                const hslVal = hexToHslNumbers(val);
                if (hslVal) {
                  if (key === '--primary') {
                    css += `  --primary: ${hslVal} !important;\n`;
                    css += `  --primary-color: ${val} !important;\n`;
                  } else if (key === '--background') {
                    css += `  --background: ${hslVal} !important;\n`;
                    css += `  --background-color: ${val} !important;\n`;
                  } else if (key === '--card') {
                    css += `  --card: ${hslVal} !important;\n`;
                    css += `  --card-color: ${val} !important;\n`;
                  } else if (key === '--border-color') {
                    css += `  --border: ${hslVal} !important;\n`;
                    css += `  --border-color: ${val} !important;\n`;
                  } else if (key === '--text-main') {
                    css += `  --foreground: ${hslVal} !important;\n`;
                    css += `  --card-foreground: ${hslVal} !important;\n`;
                    css += `  --popover-foreground: ${hslVal} !important;\n`;
                    css += `  --text-main: ${val} !important;\n`;
                  } else {
                    css += `  ${key}: ${val} !important;\n`;
                  }
                } else {
                  css += `  ${key}: ${val} !important;\n`;
                }
              } else {
                css += `  ${key}: ${val} !important;\n`;
              }
            });
          } catch (e) {
            console.warn('[ThemeLoader] Erro ao parsear cores:', e);
          }
          return css;
        };

        let cssText = '';

        // Mesclar Light Colors
        if (data.coresLight) {
          cssText += `:root {\n`;
          cssText += parseAndFormatColors(data.coresLight);
          cssText += `}\n`;
        }

        // Mesclar Dark Colors
        if (data.coresDark) {
          cssText += `.dark {\n`;
          cssText += parseAndFormatColors(data.coresDark);
          cssText += `}\n`;
        }

        // Mesclar estilos gerais (fontFamily, borderRadius, brandByProfile, etc.)
        let brandByProfile: Partial<Record<BrandProfileKey, string>> | undefined;
        let brandGradientByProfile: Partial<Record<BrandProfileKey, string>> | undefined;
        if (data.estilos) {
          const estilos = JSON.parse(data.estilos);
          let extraCss = ':root {\n';
          let hasExtra = false;
          if (estilos.fontFamily) {
            extraCss += `  --font-family: ${estilos.fontFamily} !important;\n`;
            extraCss += `  font-family: var(--font-family), sans-serif !important;\n`;
            hasExtra = true;
          }
          if (estilos.borderRadius) {
            extraCss += `  --radius: ${estilos.borderRadius} !important;\n`;
            hasExtra = true;
          }
          extraCss += '}\n';
          if (hasExtra) {
            cssText += extraCss;
          }
          if (estilos.brandByProfile && typeof estilos.brandByProfile === 'object') {
            brandByProfile = estilos.brandByProfile;
          }
          if (estilos.brandGradientByProfile && typeof estilos.brandGradientByProfile === 'object') {
            brandGradientByProfile = estilos.brandGradientByProfile;
          }
        }

        styleTag.innerHTML = cssText;

        // Cores por perfil (sidebar + desktop) — defaults do ambiente desktop + override Master
        applyBrandByProfileToDom(brandByProfile, brandGradientByProfile);

        // Tratar Textos customizados se houver
        if (data.textos) {
          const textos = JSON.parse(data.textos);
          (window as any).__ac_custom_texts = textos;
        } else {
          (window as any).__ac_custom_texts = {};
        }

        // Tratar Assets (logoUrl, etc.)
        if (data.assets) {
          const assets = JSON.parse(data.assets);
          (window as any).__ac_custom_assets = assets;
        } else {
          (window as any).__ac_custom_assets = {};
        }

        // Despachar evento para notificar componentes de que o tema mudou
        window.dispatchEvent(new Event('ac-theme-updated'));
      } catch (err) {
        console.warn('[ThemeLoader] Erro ao carregar ou aplicar temas:', err);
        // Garante defaults de marca mesmo se a API falhar
        applyBrandByProfileToDom();
        window.dispatchEvent(new Event('ac-theme-updated'));
      }
    };

    // Defaults imediatos antes da API (evita flash da cor antiga)
    applyBrandByProfileToDom();
    fetchAndApplyTheme();

    // Master Customização / reset pedem reload sem remount
    const onReload = () => {
      void fetchAndApplyTheme();
    };
    window.addEventListener('ac-theme-reload', onReload);
    return () => window.removeEventListener('ac-theme-reload', onReload);
  }, [isAuthenticated, user]);

  return null;
}

/** Helper global para tradução de textos estáticos no frontend */
export function getCustomText(key: string, defaultText: string): string {
  const custom = (window as any).__ac_custom_texts;
  return (custom && custom[key]) ? custom[key] : defaultText;
}

/** Helper global para obter urls de assets (ex: logoUrl) */
export function getCustomAsset(key: string, defaultAsset: string): string {
  const custom = (window as any).__ac_custom_assets;
  return (custom && custom[key]) ? custom[key] : defaultAsset;
}
